/**
 * The virtualised cue table.
 *
 * One component, four consumers: the convert preview, the QC report, the
 * per-range shift editor and the batch list. That is why it takes rows through
 * a callback rather than an array — the QC report wants a filtered view over a
 * document the worker owns, and copying 40,000 rows to the main thread to
 * render 30 of them is the exact mistake `contracts/adapter.md` §1 exists to
 * prevent.
 *
 * The arithmetic is in `virtual.ts` and is tested separately. What is here is
 * the DOM: a fixed pool of row elements, recycled, plus one transform per
 * frame.
 *
 * Accessibility: a windowed list lies to a screen reader by default, because
 * the accessibility tree only sees what is in the DOM. `role="grid"` with
 * `aria-rowcount` on the container and `aria-rowindex` on each row is what
 * restores the truth — "row 4,182 of 40,000" instead of "row 4 of 31".
 */

import { must, qs, setData } from './dom.ts';
import { poolSize, type Range, sameRange, sizerHeight, slotFor, windowRange } from './virtual.ts';

export interface CueRow {
  /**
   * The cue's id, never rendered. A report keyed by cue id (QC's `flagsByCue`)
   * can only decorate a row it can match, and the display index is not unique
   * across tracks.
   */
  readonly id?: string;
  /** 1-based display index; not necessarily the array position in a filter. */
  readonly n: number;
  readonly time: string;
  /**
   * The start alone, as `hh:mm:ss.mmm`: the form a duration box reads back. What a pick
   * hands over on /sync-subtitles.
   */
  readonly start?: string;
  readonly text: string;
  /** CPS or similar, already formatted. Only rendered on a metrics table. */
  readonly metric?: string;
  /** Renders the metric in the error tone — the "over threshold" flag. */
  readonly over?: boolean;
}

export interface CueTableSource {
  count: () => number;
  /** Must be cheap and synchronous. Return null for a row not yet loaded. */
  row: (index: number) => CueRow | null;
}

export interface CueTableOptions {
  /**
   * Makes the rows controls: a click, or Enter or Space on a focused row, hands that row
   * over. Only /sync-subtitles passes it. Without it the rows take no focus and answer no
   * click, which is every other table on the site.
   */
  readonly onPick?: (row: CueRow) => void;
}

export interface CueTableHandle {
  /** Re-read count and repaint. Call after the source changes. */
  refresh: () => void;
  /** Scroll a row into view without changing focus. */
  scrollToRow: (index: number) => void;
  destroy: () => void;
}

const OVERSCAN = 6;
/** Used only until a real row can be measured. */
const FALLBACK_ROW_HEIGHT = 44;

interface RowNodes {
  root: HTMLElement;
  idx: HTMLElement;
  time: HTMLElement;
  text: HTMLElement;
  metric: HTMLElement | null;
  /** The row index this element currently renders, or -1 when parked. */
  index: number;
}

function buildRow(withMetric: boolean, pickable: boolean): RowNodes {
  const root = document.createElement('div');
  root.className = 'cue-table__row cue-table__row--virtual';
  root.setAttribute('role', 'row');
  if (pickable) {
    root.classList.add('cue-table__row--pick');
    // In the tab order, one stop per row on screen: the window holds a screenful plus the
    // overscan, so Tab walks the lines the user can see.
    root.tabIndex = 0;
  }

  const cell = (mod: string) => {
    const node = document.createElement('span');
    node.className = `cue-table__cell cue-table__cell--${mod}`;
    node.setAttribute('role', 'gridcell');
    root.append(node);
    return node;
  };

  const idx = cell('idx');
  const time = cell('time');
  const text = cell('text');
  const metric = withMetric ? cell('metric') : null;
  return { root, idx, time, text, metric, index: -1 };
}

export function mountCueTable(
  container: HTMLElement,
  source: CueTableSource,
  options: CueTableOptions = {},
): CueTableHandle {
  const scroller = must<HTMLElement>('[data-cue-scroller]', container);
  const sizer = must<HTMLElement>('[data-cue-sizer]', container);
  const windowEl = must<HTMLElement>('[data-cue-window]', container);
  const empty = qs<HTMLElement>('[data-cue-empty]', container);
  const withMetric = container.classList.contains('cue-table--metrics');
  const { onPick } = options;

  const pool: RowNodes[] = [];
  let rowHeight = FALLBACK_ROW_HEIGHT;
  let rendered: Range = { start: 0, end: 0, offset: 0 };
  let frame = 0;
  let destroyed = false;

  /**
   * Measure one real row rather than trusting a constant. Row height changes
   * with the viewport (the layout stacks below 600px) and with the user's own
   * font size, and a hardcoded number gets both wrong.
   */
  function measure(): void {
    const probe = pool[0];
    if (!probe || probe.root.hidden) return;
    // A container with no layout — a background tab, a display:none ancestor,
    // a details element that has not been opened — reports zero width. Rows
    // are absolutely positioned inside it, so at zero width every cue wraps to
    // one character per line and measures at forty times its real height. That
    // number then becomes the sizer height, and the scrollbar becomes a lie
    // that survives until the next resize. Measured in a hidden tab: 1,945px
    // per row and a 77,800,000px sizer for a list that is 1,760,000px tall.
    if (scroller.clientWidth === 0 || scroller.clientHeight === 0) return;
    const height = probe.root.getBoundingClientRect().height;
    if (height > 0 && Math.abs(height - rowHeight) > 0.5) {
      rowHeight = height;
      sizer.style.height = `${sizerHeight(source.count(), rowHeight)}px`;
      // Every row's position was computed from the old height.
      for (const row of pool) row.index = -1;
    }
  }

  function ensurePool(size: number): void {
    while (pool.length < size) {
      const row = buildRow(withMetric, onPick !== undefined);
      pool.push(row);
      windowEl.append(row.root);
    }
    // The pool only ever grows. A viewport that shrank keeps its spare rows
    // parked rather than destroying nodes it is about to need again on the
    // next rotation. Changing the pool length changes every slot assignment,
    // so parked rows are invalidated.
    for (let i = size; i < pool.length; i += 1) {
      const row = pool[i];
      if (row && !row.root.hidden) {
        row.root.hidden = true;
        row.index = -1;
      }
    }
  }

  function write(node: RowNodes, index: number, data: CueRow): void {
    node.root.hidden = false;
    node.root.style.transform = `translate3d(0, ${index * rowHeight}px, 0)`;
    node.root.setAttribute('aria-rowindex', String(index + 1));
    node.idx.textContent = String(data.n);
    node.time.textContent = data.time;
    node.text.textContent = data.text;
    if (node.metric) {
      node.metric.textContent = data.metric ?? '';
      if (data.over) setData(node.metric, 'over', 'true');
      else setData(node.metric, 'over', null);
    }
    node.index = index;
  }

  function paint(): void {
    if (destroyed) return;
    const count = source.count();
    const viewport = scroller.clientHeight || 1;
    const range = windowRange(scroller.scrollTop, viewport, rowHeight, count, OVERSCAN);
    const wanted = Math.min(poolSize(viewport, rowHeight, OVERSCAN), Math.max(count, 1));
    const grew = wanted > pool.length;
    ensurePool(wanted);
    if (grew) for (const row of pool) row.index = -1;

    // Modulo slot assignment: a row keeps its content until its index changes,
    // so scrolling by one row costs one row's writes rather than a windowful.
    for (let index = range.start; index < range.end; index += 1) {
      const node = pool[slotFor(index, pool.length)];
      if (!node || node.index === index) continue;
      const data = source.row(index);
      if (!data) {
        node.root.hidden = true;
        node.index = -1;
        continue;
      }
      write(node, index, data);
    }
    // Park anything the window has left behind.
    for (const node of pool) {
      if (node.index >= range.start && node.index < range.end) continue;
      if (node.root.hidden) continue;
      node.root.hidden = true;
      node.index = -1;
    }
    rendered = range;
  }

  function onScroll(): void {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const next = windowRange(
        scroller.scrollTop,
        scroller.clientHeight || 1,
        rowHeight,
        source.count(),
        OVERSCAN,
      );
      // A scroll inside the overscan margin needs no DOM writes at all.
      if (sameRange(next, rendered)) return;
      paint();
    });
  }

  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          measure();
          paint();
        })
      : null;

  /** The row an event landed in, as the table currently shows it. */
  function rowOf(target: EventTarget | null): CueRow | null {
    const hit = target instanceof Element ? target.closest('.cue-table__row') : null;
    const node = pool.find((row) => row.root === hit);
    return node === undefined || node.index < 0 ? null : source.row(node.index);
  }

  // Delegated, once, rather than per row: the rows are a recycled pool, and a listener per
  // element would answer for whichever cue the element showed when it was bound.
  function onClick(ev: Event): void {
    const row = rowOf(ev.target);
    if (row) onPick?.(row);
  }

  function onKey(ev: KeyboardEvent): void {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    // The row itself, focused: what a button answers to, and nothing inside it.
    if (!(ev.target instanceof HTMLElement) || !ev.target.matches('.cue-table__row--pick')) return;
    const row = rowOf(ev.target);
    if (!row) return;
    // Space would otherwise scroll the table out from under the row just picked.
    ev.preventDefault();
    onPick?.(row);
  }

  if (onPick) {
    windowEl.addEventListener('click', onClick);
    windowEl.addEventListener('keydown', onKey);
  }

  scroller.addEventListener('scroll', onScroll, { passive: true });
  resizeObserver?.observe(scroller);

  const handle: CueTableHandle = {
    refresh() {
      const count = source.count();
      container.setAttribute('aria-rowcount', String(count));
      if (empty) empty.hidden = count > 0;
      scroller.hidden = count === 0;
      sizer.style.height = `${sizerHeight(count, rowHeight)}px`;
      rendered = { start: -1, end: -1, offset: 0 };
      for (const row of pool) row.index = -1;
      paint();
      measure();
      paint();
    },
    scrollToRow(index) {
      scroller.scrollTop = Math.max(0, index * rowHeight);
      paint();
    },
    destroy() {
      destroyed = true;
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', onScroll);
      windowEl.removeEventListener('click', onClick);
      windowEl.removeEventListener('keydown', onKey);
      resizeObserver?.disconnect();
      windowEl.replaceChildren();
      pool.length = 0;
    },
  };

  handle.refresh();
  return handle;
}
